"use client";

import { useEffect, useRef, useState } from "react";
import type { AsciiSceneHandle } from "./asciiSceneRenderer";
import { ASCII_SCENES, type AsciiSceneName, type AsciiScenePreset, type AsciiSceneTheme } from "./scenes";
import styles from "./AsciiSceneBackground.module.css";

/** Any CSS colour as rgb(), via a 1px canvas (tokens may be hex, rgb, oklch, ...). */
function toRgb(value: string, probe: CanvasRenderingContext2D | null): string {
  if (!probe || !value) return value;
  probe.clearRect(0, 0, 1, 1);
  probe.fillStyle = "#000";
  probe.fillStyle = value;
  probe.fillRect(0, 0, 1, 1);
  const [r, g, b] = probe.getImageData(0, 0, 1, 1).data;
  return `rgb(${r}, ${g}, ${b})`;
}

/** The scene's colours as they resolve at the canvas, so hero-scoped tokens apply. */
function readColors(el: HTMLElement, preset: AsciiScenePreset, probe: CanvasRenderingContext2D | null) {
  const css = getComputedStyle(el);
  const fg = toRgb(css.getPropertyValue(preset.colors.fg).trim() || css.color, probe);
  const bg = toRgb(css.getPropertyValue(preset.colors.bg).trim() || css.backgroundColor, probe);
  const theme = document.documentElement.dataset.theme ?? "";
  const strength =
    theme in preset.strength ? preset.strength[theme as AsciiSceneTheme] : preset.strength.light;
  return { fg, bg, strength };
}

/**
 * Whether WebGL runs on a GPU. A software rasteriser (SwiftShader, llvmpipe,
 * WARP) draws every frame on the CPU: PageSpeed Insights' test machines have
 * no GPU, and the scene alone cost them 32.8s of main-thread work on desktop.
 * Browsers that mask the renderer name count as hardware. This probe is
 * cheap and runs before three.js is downloaded.
 */
function hasHardwareWebGL(): boolean {
  const probe = document.createElement("canvas");
  const gl = (probe.getContext("webgl2") ?? probe.getContext("webgl")) as WebGLRenderingContext | null;
  if (!gl) return false;
  const info = gl.getExtension("WEBGL_debug_renderer_info");
  const name = String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
  gl.getExtension("WEBGL_lose_context")?.loseContext();
  return !/swiftshader|llvmpipe|softpipe|software|basic render|warp/i.test(name);
}

export interface AsciiSceneBackgroundProps {
  /** Which bench-tuned scene to draw. */
  scene: AsciiSceneName;
  className?: string;
}

/**
 * Hero background: a 3D model rendered as ASCII that moves on its own and can
 * be dragged sideways to rotate. Decorative only.
 *
 * three.js and the model load after the load event, once the page is idle,
 * so they never compete with the hero text or hydration. No hardware WebGL, a
 * failed load or forced colours leave the plain hero background. Reduced
 * motion holds the model still.
 */
export function AsciiSceneBackground({ scene, className }: AsciiSceneBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof window === "undefined") return;
    if (window.matchMedia("(forced-colors: active)").matches) return;

    const preset = ASCII_SCENES[scene];
    let probe: CanvasRenderingContext2D | null = null;
    const colors = () => {
      if (!probe) {
        const probeCanvas = document.createElement("canvas");
        probeCanvas.width = probeCanvas.height = 1;
        probe = probeCanvas.getContext("2d", { willReadFrequently: true });
      }
      return readColors(canvas, preset, probe);
    };

    let handle: AsciiSceneHandle | null = null;
    let cancelled = false;
    let visible = true;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const shouldRun = () => visible && !document.hidden && !motion.matches;

    const start = async () => {
      try {
        const { createAsciiScene } = await import("./asciiSceneRenderer");
        if (cancelled) return;
        const { fg, bg, strength } = colors();
        handle = await createAsciiScene(canvas, {
          modelUrl: preset.modelUrl,
          fg,
          bg,
          strength,
          cell: preset.cell,
          tone: preset.tone,
          light: preset.light,
          zoom: preset.zoom,
          offset: preset.offset,
          motion: preset.motion,
        });
        if (cancelled) {
          handle.destroy();
          return;
        }
        handle.setRunning(shouldRun());
        setReady(true);
      } catch {
        // No WebGL or the model failed: the hero background stands in.
      }
    };

    // After the load event, then idle: three.js (620 KB) and the model never
    // compete with hydration or the page's own images.
    let idle: number | undefined;
    const schedule = () => {
      if (cancelled) return;
      if (!hasHardwareWebGL()) return;
      idle = window.requestIdleCallback
        ? window.requestIdleCallback(() => void start(), { timeout: 2500 })
        : window.setTimeout(() => void start(), 1200);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    const io = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? true;
      handle?.setRunning(shouldRun());
    });
    io.observe(canvas);
    const onVisibility = () => handle?.setRunning(shouldRun());
    document.addEventListener("visibilitychange", onVisibility);
    motion.addEventListener("change", onVisibility);

    // Follow theme switches (class / data-theme on <html>).
    const themeObserver = new MutationObserver(() => {
      if (!handle) return;
      const { fg, bg, strength } = colors();
      handle.setColors(fg, bg, strength);
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme"],
    });

    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      if (idle !== undefined) {
        if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
        window.clearTimeout(idle);
      }
      io.disconnect();
      themeObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      motion.removeEventListener("change", onVisibility);
      handle?.destroy();
    };
  }, [scene]);

  return (
    <canvas
      ref={canvasRef}
      className={[styles.canvas, ready ? styles.ready : "", className ?? ""].join(" ").trim()}
      aria-hidden="true"
      data-ascii-scene={scene}
    />
  );
}
